import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const BASE_URL = 'https://www.onwardland.com';

/**
 * SEO Head component - Sets canonical URL, meta title, description, and Open Graph tags
 * @param {string} title - Page title
 * @param {string} description - Meta description
 * @param {string} canonicalPath - Optional custom canonical path (defaults to current path)
 * @param {string} ogTitle - Open Graph title (defaults to title)
 * @param {string} ogDescription - Open Graph description (defaults to description)
 * @param {boolean} noIndex - Set to true to add noindex meta tag (for admin pages)
 */
export const useSEO = ({ 
  title, 
  description, 
  canonicalPath = null, 
  ogTitle = null, 
  ogDescription = null,
  ogImage = null,
  noIndex = false 
}) => {
  const location = useLocation();
  
  useEffect(() => {
    // Set page title
    if (title) {
      document.title = title;
    }
    
    // Set meta description
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = description;
    }
    
    // Set canonical URL - always use www version
    const path = canonicalPath || location.pathname;
    const canonicalUrl = `${BASE_URL}${path === '/' ? '' : path}`;
    
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.rel = 'canonical';
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.href = canonicalUrl;
    
    // Update Open Graph URL
    let ogUrl = document.querySelector('meta[property="og:url"]');
    if (!ogUrl) {
      ogUrl = document.createElement('meta');
      ogUrl.setAttribute('property', 'og:url');
      document.head.appendChild(ogUrl);
    }
    ogUrl.content = canonicalUrl;
    
    // Update Open Graph Title
    const finalOgTitle = ogTitle || title;
    if (finalOgTitle) {
      let ogTitleMeta = document.querySelector('meta[property="og:title"]');
      if (!ogTitleMeta) {
        ogTitleMeta = document.createElement('meta');
        ogTitleMeta.setAttribute('property', 'og:title');
        document.head.appendChild(ogTitleMeta);
      }
      ogTitleMeta.content = finalOgTitle;
    }
    
    // Update Open Graph Description
    const finalOgDescription = ogDescription || description;
    if (finalOgDescription) {
      let ogDescMeta = document.querySelector('meta[property="og:description"]');
      if (!ogDescMeta) {
        ogDescMeta = document.createElement('meta');
        ogDescMeta.setAttribute('property', 'og:description');
        document.head.appendChild(ogDescMeta);
      }
      ogDescMeta.content = finalOgDescription;
    }
    
    // Update Twitter Title
    if (finalOgTitle) {
      let twitterTitle = document.querySelector('meta[name="twitter:title"]');
      if (!twitterTitle) {
        twitterTitle = document.createElement('meta');
        twitterTitle.name = 'twitter:title';
        document.head.appendChild(twitterTitle);
      }
      twitterTitle.content = finalOgTitle;
    }
    
    // Update Twitter Description
    if (finalOgDescription) {
      let twitterDesc = document.querySelector('meta[name="twitter:description"]');
      if (!twitterDesc) {
        twitterDesc = document.createElement('meta');
        twitterDesc.name = 'twitter:description';
        document.head.appendChild(twitterDesc);
      }
      twitterDesc.content = finalOgDescription;
    }
    
    // Update Twitter URL
    let twitterUrl = document.querySelector('meta[name="twitter:url"]');
    if (!twitterUrl) {
      twitterUrl = document.createElement('meta');
      twitterUrl.name = 'twitter:url';
      document.head.appendChild(twitterUrl);
    }
    twitterUrl.content = canonicalUrl;
    
    // Update Open Graph Image
    if (ogImage) {
      let ogImageMeta = document.querySelector('meta[property="og:image"]');
      if (!ogImageMeta) {
        ogImageMeta = document.createElement('meta');
        ogImageMeta.setAttribute('property', 'og:image');
        document.head.appendChild(ogImageMeta);
      }
      ogImageMeta.content = ogImage;

      let twitterImage = document.querySelector('meta[name="twitter:image"]');
      if (!twitterImage) {
        twitterImage = document.createElement('meta');
        twitterImage.name = 'twitter:image';
        document.head.appendChild(twitterImage);
      }
      twitterImage.content = ogImage;

      let twitterCard = document.querySelector('meta[name="twitter:card"]');
      if (!twitterCard) {
        twitterCard = document.createElement('meta');
        twitterCard.name = 'twitter:card';
        document.head.appendChild(twitterCard);
      }
      twitterCard.content = 'summary_large_image';
    }
    
    // Handle robots meta tag for noindex
    let robotsMeta = document.querySelector('meta[name="robots"]');
    if (noIndex) {
      if (!robotsMeta) {
        robotsMeta = document.createElement('meta');
        robotsMeta.name = 'robots';
        document.head.appendChild(robotsMeta);
      }
      robotsMeta.content = 'noindex, nofollow';
    } else {
      if (robotsMeta) {
        robotsMeta.content = 'index, follow';
      }
    }
    
  }, [title, description, canonicalPath, ogTitle, ogDescription, ogImage, noIndex, location.pathname]);
};

/**
 * Image optimization helper - Returns optimized image props
 */
export const getOptimizedImageProps = (src, alt, priority = false) => {
  return {
    src,
    alt,
    loading: priority ? 'eager' : 'lazy',
    decoding: 'async',
    ...(priority && { fetchPriority: 'high' }),
  };
};

export default useSEO;
